import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment'

import { QuestionCircleFilled } from '@ant-design/icons'
import { colours } from '@qflow/theme'

import './Settings.css'
import {
  Badge,
  Table,
  Modal,
  Alert,
  Select,
  Form,
  Switch,
  Tooltip,
  Row,
  Col
} from 'antd'
import { FilterFilled } from '@ant-design/icons'

import { accountLoad } from 'portal/store/accountDetails/actions'
import { matchesEmailFilter } from 'portal/lib/functions/userSettingsHelpers'
import {
  ColourButton,
  icons,
  UserCard,
  UserAvatar,
  ClickableLink
} from 'portal/lib/primitives'
import {
  getIdFromRole,
  ROLE_INFORMATION,
  USER_ROLES
} from 'portal/lib/constants/UserRole'

import { SmallSpinner } from 'portal/lib/primitives'
import { FORBIDDEN } from 'portal/routes'
import { projectUsersLoad } from 'portal/store/projectUsers/actions'
import { useUserProjectRole } from 'portal/lib/hooks'
import {
  projectUserEditStart,
  projectUsersUpdateStart
} from 'portal/store/projectUserDetails/actions'
import { getBrowserShortDateFormat } from 'portal/lib/functions/dateFormatHelpers'

const areNotificationsEnabledForCurrentProject = user => {
  if (
    user.notificationsEnabled &&
    user.projectSettings.length > 0 &&
    user.projectSettings[0].notificationsEnabled
  ) {
    return true
  }
  return false
}

const getTableData = usersState => {
  const data = []
  if (usersState.loading == null) {
    return data
  }

  const minDate = moment.utc('0001-01-01')

  Object.values(usersState.users).forEach(user => {
    let role = 'No Access'
    if (user.projectPermissions.length > 0) {
      role = USER_ROLES[user.projectPermissions[0].userRoleId]
    }

    data.push({
      userId: user.userId,
      displayName: user.displayName,
      email: user.email,
      role: role,
      notificationsEnabled: areNotificationsEnabledForCurrentProject(user),
      lastActivity:
        user.lastActivity && moment.utc(user.lastActivity).isAfter(minDate)
          ? moment(user.lastActivity)
              .local()
              .format(getBrowserShortDateFormat())
          : 'No activity',
      userData: user
    })
  })

  data.sort((a, b) => {
    if (a.displayName && b.displayName) {
      return a.displayName
        .toUpperCase()
        .localeCompare(b.displayName.toUpperCase())
    }
    return 0
  })

  return data
}

export const ProjectSettingsTable = () => {
  const [filteredInfo, setFilteredInfo] = useState({})

  const dispatch = useDispatch()
  const { accountId, projectId } = useParams()
  const navigate = useNavigate()
  const users = useSelector(state => state.projectUsers)
  const projectUser = useSelector(state => state.projectUserDetails)
  const auth = useSelector(state => state.auth)

  const { isProjectAdmin, roleLoaded } = useUserProjectRole(
    accountId,
    projectId
  )

  const [form] = Form.useForm()
  const [formRoleFieldDisabled, setFormRoleFieldDisabled] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [alert, setAlert] = useState(null)

  useEffect(() => {
    dispatch(accountLoad(accountId))
  }, [accountId, dispatch, projectId])

  if (roleLoaded && !isProjectAdmin) {
    navigate(FORBIDDEN)
  }

  useEffect(() => {
    if (!projectUser.loading && projectUser.error != null) {
      setAlert(
        <Alert
          key="error"
          message={'Something Went Wrong'}
          description={'Please try submitting again.'}
          type="error"
          closable
        />
      )
    }
    if (!projectUser.loading && projectUser.error == null) {
      setAlert(null)
      setModalOpen(false)
      dispatch(projectUsersLoad(accountId, projectId, true))
    }
  }, [projectUser.loading, projectUser.error, dispatch, accountId, projectId])

  const getFormData = user => {
    let accessEnabled = false
    let role = 'Reader'
    if (user.projectPermissions.length > 0) {
      accessEnabled = true
      role = USER_ROLES[user.projectPermissions[0].userRoleId]
    }
    setFormRoleFieldDisabled(!accessEnabled)

    return {
      notificationsEnabled: areNotificationsEnabledForCurrentProject(user),
      accessEnabled: accessEnabled,
      role: role
    }
  }

  const projectUsersFiltered = getTableData(users)
    ?.filter(user =>
      matchesEmailFilter(auth.isQualisFlowInternal, '', user.email)
    )
    .sort((a, b) => {
      if (a.displayName && b.displayName) {
        return a.displayName
          .toUpperCase()
          .localeCompare(b.displayName.toUpperCase())
      }
      return 0
    })

  // Create filter options for the table's Users column based on the users in the account and
  // deduplicate them in case of users with same display name
  const uniqueDisplayNames = new Set()
  const projectUsersFilteredForTable = projectUsersFiltered
    .filter(user => {
      if (uniqueDisplayNames.has(user.displayName)) {
        return false
      }
      uniqueDisplayNames.add(user.displayName)
      return true
    })
    .map(user => {
      return { text: user.displayName, value: user.displayName }
    })

  // Create filter options for the Table's Email address column based on the users in the account
  const projectUsersFilteredForTableEmail = projectUsersFiltered.map(user => {
    return { text: user.email, value: user.email }
  })

  // Create filter options for the Table's Role column
  const projectRoleForTable = useMemo(() => {
    const roles = Object.values(USER_ROLES).map(role => {
      return { text: role, value: role }
    })
    roles.unshift({ text: 'No Access', value: 'No Access' })
    return roles
  }, [])

  const handleFormSubmit = () => {
    form.validateFields().then(values => {
      const projectPermissions = []
      if (values.accessEnabled) {
        projectPermissions.push({
          userId: projectUser.userId,
          projectId: projectId,
          userRoleId: getIdFromRole(values.role)
        })
      }

      const notificationsEnabled =
        projectUser.notificationsEnabled || values.notificationsEnabled

      const projectSettings = [
        {
          userId: projectUser.userId,
          projectId: projectId,
          notificationsEnabled: values.notificationsEnabled
        }
      ]
      setAlert(null)
      dispatch(
        projectUsersUpdateStart(
          accountId,
          projectId,
          projectUser.userId,
          projectPermissions,
          notificationsEnabled,
          projectSettings
        )
      )
    })
  }

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // `extra.currentDataSource` contains the filtered data
    setFilteredInfo(filters)
  }

  const handleResetFilters = () => {
    setFilteredInfo({})
  }

  const handleCancel = () => {
    setModalOpen(false)
    setAlert(null)
    dispatch(projectUserEditStart({}))
  }

  // If all filters are null, empty string, or empty array, disable the reset button
  const allFiltersEmpty = Object.entries(filteredInfo).every(
    ([key, filter]) => {
      return (
        filter === null ||
        filter === '' ||
        (Array.isArray(filter) && filter.length === 0)
      )
    }
  )

  // Count the number of active filters to display in the badge
  const activeFilterCount = Object.entries(filteredInfo).reduce(
    (count, [key, filter]) => {
      if (
        (typeof filter === 'string' && filter !== '') ||
        (Array.isArray(filter) && filter.length > 0)
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  const columns = [
    {
      width: '2rem',
      render: data => {
        return (
          <UserAvatar displayName={data.displayName} displayTooltip={false} />
        )
      }
    },
    {
      title: 'User',
      dataIndex: 'displayName',
      key: 'displayName',
      filters: projectUsersFilteredForTable,
      filteredValue: filteredInfo.displayName || null,
      filterSearch: true,
      onFilter: (value, record) => {
        if (record.displayName) {
          return record.displayName.includes(value)
        }
        return false
      },
      filterIcon: filtered => {
        const count = filteredInfo.displayName?.length || 0
        return (
          <Tooltip
            title={
              count > 0
                ? `${count} filter option${count > 1 ? 's' : ''} applied`
                : 'No filters applied'
            }
          >
            <FilterFilled
              style={{ color: filtered ? colours.TEAL : undefined }}
            />
          </Tooltip>
        )
      }
    },
    {
      width: '10rem',
      title: () => {
        let roleDescriptions = 'The following options are:'
        Object.values(USER_ROLES).forEach(role => {
          roleDescriptions += `\n    • ${role}: ${ROLE_INFORMATION[role]}`
        })
        return (
          <>
            {'Role'}
            <Tooltip
              styles={{ root: { whiteSpace: 'pre-line' } }}
              title={`This corresponds to the permission type your user has. \n\n ${roleDescriptions}`}
              placement="right"
            >
              <QuestionCircleFilled
                style={{
                  color: colours.TEAL,
                  float: 'right',
                  marginTop: '3px'
                }}
              />
            </Tooltip>
          </>
        )
      },
      dataIndex: 'role',
      key: 'role',
      filters: projectRoleForTable,
      filteredValue: filteredInfo.role || null,
      onFilter: (value, record) => {
        if (record.role) {
          return record.role.includes(value)
        }
        return false
      },
      filterIcon: filtered => {
        const count = filteredInfo.role?.length || 0
        return (
          <Tooltip
            title={
              count > 0
                ? `${count} filter option${count > 1 ? 's' : ''} applied`
                : 'No filters applied'
            }
          >
            <FilterFilled
              style={{ color: filtered ? colours.TEAL : undefined }}
            />
          </Tooltip>
        )
      }
    },
    {
      width: '10rem',
      title: () => {
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {'Notifications'}
            <Tooltip
              styles={{ root: { whiteSpace: 'pre-line' } }}
              title={`This corresponds to whether the user receives project specific notifications. \n
              If enabled, they will receive an email notification each time an event is raised for a record in this project.`}
              placement="right"
            >
              <QuestionCircleFilled
                style={{
                  color: colours.TEAL,
                  float: 'right',
                  marginTop: '3px'
                }}
              />
            </Tooltip>
          </div>
        )
      },
      dataIndex: 'notificationsEnabled',
      key: 'notificationsEnabled',
      render: (_, user, index) => {
        return user.notificationsEnabled ? 'Enabled' : 'Disabled'
      }
    },
    {
      title: 'Email address',
      dataIndex: 'email',
      key: 'email',
      filters: projectUsersFilteredForTableEmail,
      filteredValue: filteredInfo.email || null,
      filterSearch: true,
      onFilter: (value, record) => {
        if (record.email) {
          return record.email.includes(value)
        }
        return false
      },
      filterIcon: filtered => {
        const count = filteredInfo.email?.length || 0
        return (
          <Tooltip
            title={
              count > 0
                ? `${count} filter option${count > 1 ? 's' : ''} applied`
                : 'No filters applied'
            }
          >
            <FilterFilled
              style={{ color: filtered ? colours.TEAL : undefined }}
            />
          </Tooltip>
        )
      }
    },
    {
      width: '15rem',
      title: 'Last activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity'
    },
    {
      title: (
        <Tooltip
          title={allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'}
        >
          <ResetFilters
            aria-label={
              allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
            }
            disabled={allFiltersEmpty}
            onClick={handleResetFilters}
          >
            <Badge
              count={allFiltersEmpty ? 0 : activeFilterCount}
              offset={[-1, 1]}
              color={colours.TEAL}
              styles={{
                root: {
                  color: 'inherit'
                }
              }}
            >
              <icons.ResetFilters size="22px" />
            </Badge>
          </ResetFilters>
        </Tooltip>
      ),
      width: '2rem',
      render: data => {
        return (
          <StyledClickableLink
            id="navigateToDetailsBtn"
            onClick={() => {
              dispatch(projectUserEditStart(data.userData))
              form.setFieldsValue(getFormData(data.userData))
              setModalOpen(true)
            }}
            to={'#'}
            style={{ padding: '0.25rem' }}
          >
            <Tooltip title="Edit details">
              <icons.ViewRecord />
            </Tooltip>
          </StyledClickableLink>
        )
      }
    }
  ]

  return (
    <>
      <Modal
        open={modalOpen}
        onOk={handleFormSubmit}
        onCancel={handleCancel}
        confirmLoading={projectUser.loading}
        title={
          <UserCard
            displayName={projectUser?.displayName}
            email={projectUser?.email}
          />
        }
        transitionName=""
        footer={[
          <ColourButton.Basic
            key="back"
            type="button"
            onClick={handleCancel}
            style={{ display: 'inline-block', marginRight: '.75rem' }}
          >
            Cancel
          </ColourButton.Basic>,
          <ColourButton.Action
            key="submit"
            type="button"
            onClick={handleFormSubmit}
            style={{ display: 'inline-block' }}
          >
            OK
          </ColourButton.Action>
        ]}
      >
        <Form
          form={form}
          name="userEdit"
          layout="vertical"
          requiredMark={false}
          onValuesChange={values => {
            if (values.accessEnabled === false) {
              form.setFieldsValue({ notificationsEnabled: false })
            }
            if (values.notificationsEnabled === true) {
              form.setFieldsValue({ accessEnabled: true })
            }
            setFormRoleFieldDisabled(!form.getFieldValue('accessEnabled'))
          }}
        >
          <Form.Item
            name="accessEnabled"
            label="Access Enabled"
            valuePropName="checked"
          >
            <Switch loading={projectUser.loading} />
          </Form.Item>
          <Form.Item
            name="notificationsEnabled"
            label="Notifications Enabled"
            valuePropName="checked"
          >
            <Switch loading={projectUser.loading} />
          </Form.Item>
          <Form.Item name="role" label="Role" style={{ marginTop: '-15px' }}>
            <Select
              style={{ width: '100%' }}
              disabled={formRoleFieldDisabled || projectUser.loading}
              loading={projectUser.loading}
            >
              {Object.values(USER_ROLES).map(value => (
                <Select.Option value={value} key={value}>
                  <Row>
                    <Col span={8}>{value}</Col>
                    <Col span={1} offset={15}>
                      <Tooltip
                        title={ROLE_INFORMATION[value]}
                        placement="right"
                      >
                        <QuestionCircleFilled
                          style={{
                            color: colours.TEAL
                          }}
                        />
                      </Tooltip>
                    </Col>
                  </Row>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        {alert}
      </Modal>
      <FlexWrapperPanel>
        <Table
          rowKey="userId"
          columns={columns}
          onChange={handleTableChange}
          dataSource={projectUsersFiltered}
          pagination={false}
          rowClassName={(record, index) => {
            if (index === 0) {
              return 'table-row-filter'
            }
            return 'table-row-light'
          }}
          size="middle"
          loading={{
            spinning: users.loading,
            indicator: (
              <div>
                <SmallSpinner />
              </div>
            )
          }}
        />
      </FlexWrapperPanel>
    </>
  )
}

const FlexWrapperPanel = styled.div`
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.5rem 1.25rem 1.25rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const ResetFilters = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
const StyledClickableLink = styled(ClickableLink)`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: ${colours.TEAL};
  }
`
